import { Text, defaultTheme } from '@agro-club/agroclub-shared'
import env, { isRussia, isUS } from 'env'
import { DealStatusTypes } from 'modules/domain/deal/types'
import { formatTarifDistance } from 'modules/utils/numbers/formatDistance'
import formatNumber from 'modules/utils/numbers/formatNumber'
import formatPrice from 'modules/utils/numbers/formatPrice'
import { formatTarif } from 'modules/utils/numbers/formatTarif'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'react-responsive'
import styled from 'styled-components'
import { Badge } from 'views/components/Badge/Badge'
import { Card } from 'views/components/Card/Card'
import { KeyValue, Value } from 'views/components/KeyValue/KeyValue'
import { QuestionTooltip } from 'views/components/QuestionTooltip/QuestionTooltip'
import { FormulaTooltip } from '../../../components/FormulaTooltip'
import { hasBaseCreditData } from '../../../helpers/hasBaseCreditData'
import { useDealContext } from '../../DealContext'
import { QuestionTooltipContent } from '../../QuestionTooltipContent'
import { CreditInfo } from './CreditInfo'

const ValueAndTooltipWrapper = styled.div`
  display: flex;
  column-gap: 2px;
  align-items: center;
`
const PercentBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0 4px;
`
const Percent = styled(Text)<{ isGreen: boolean }>`
  color: ${({ isGreen }) => (isGreen ? defaultTheme.color.accentApprove600 : defaultTheme.color.accentDestruct600)};
  white-space: nowrap;
`

const Container = styled(Card.Container)`
  @media (max-width: ${defaultTheme.queries.mobile}) {
    margin: 0px 8px 8px 8px;
    width: auto;
  }
`

export const NumbersInfo = () => {
  const { t } = useTranslation('deal')
  const { deal } = useDealContext()

  const currency = env.REACT_APP_CURRENCY_SYMBOL
  const actualMarginLabel = `${t('fields.factMargin')} ${deal.margin_confirmed ? t('fields.1c_tag') : ''}`
  const isVisibleMarginTooltip = deal.margin_confirmed && deal.status === DealStatusTypes.SHIPMENT_COMPLETE && isRussia

  const hasBaseCredData = hasBaseCreditData(deal)
  const showTargetPercent = isRussia && hasBaseCredData
  const isMobile = useMediaQuery({
    query: `(max-width: ${defaultTheme.queries.mobile})`,
  })
  const maxColumnWidth = isMobile ? '' : '151px'

  const marginPercent = showTargetPercent ? (
    <PercentBlock>
      <FormulaTooltip numberFormula={deal.margin_percent_formula} textFormula={deal.margin_percent_tooltip}>
        <Percent isGreen={deal.margin_percent_is_acceptable}>{formatNumber(deal.margin_percent)} %</Percent>
      </FormulaTooltip>
      <Badge>{`${t('need')} ${formatNumber(deal.target_margin_percent)}%`}</Badge>
    </PercentBlock>
  ) : (
    <FormulaTooltip numberFormula={deal.margin_percent_formula} textFormula={deal.margin_percent_tooltip}>
      <div>{formatNumber(deal.margin_percent)} %</div>
    </FormulaTooltip>
  )

  const grossMargin = (
    <KeyValue label={t('fields.grossMargin')}>
      <FormulaTooltip numberFormula={deal.margin_formula} textFormula={deal.margin_tooltip}>
        <Value typography="accentLarge">{formatPrice(deal.margin, true)}</Value>
      </FormulaTooltip>
      {marginPercent}
      <FormulaTooltip numberFormula={deal.margin_per_kg_formula} textFormula={deal.margin_per_kg_tooltip}>
        <div>{formatTarif(deal.margin_per_kg)}</div>
      </FormulaTooltip>
    </KeyValue>
  )

  if (isUS) {
    return (
      <Container>
        <Card.Grid maxColumnWidth={maxColumnWidth} columnGap={8}>
          {grossMargin}
        </Card.Grid>
      </Container>
    )
  }

  return (
    <Container>
      <Card.Grid cols={2} maxColumnWidth={maxColumnWidth} columnGap={8}>
        <KeyValue label={t('fields.gmv')} valueTypo="accentLarge">
          <FormulaTooltip numberFormula={deal.gmv_formula} textFormula={deal.gmv_tooltip}>
            {formatPrice(deal.gmv, true)}
          </FormulaTooltip>
        </KeyValue>
        <KeyValue label={t('fields.freight_rate')} valueTypo="accentLarge">
          <FormulaTooltip
            numberFormula={deal.price_logistics_ton_per_km_formula}
            textFormula={deal.price_logistics_ton_per_km_tooltip}
          >
            {formatTarifDistance(deal.price_logistics_ton_per_km)}
          </FormulaTooltip>
        </KeyValue>
        {grossMargin}
        <KeyValue label={actualMarginLabel}>
          <Value typography="accentLarge">
            <ValueAndTooltipWrapper>
              {formatPrice(deal.actual_margin)} {t('common:rub', { currency })}
              {isVisibleMarginTooltip && (
                <QuestionTooltip size="compact" tooltipContent={<QuestionTooltipContent deal={deal} />} />
              )}
            </ValueAndTooltipWrapper>
          </Value>
          <div>{formatNumber(deal.actual_margin_percent)} %</div>
          <div>{formatTarif(deal.actual_margin_per_kg)}</div>
        </KeyValue>
        <CreditInfo deal={deal} />
      </Card.Grid>
    </Container>
  )
}
